import rison from 'rison';

import { IntegrationCategory } from '@gitbook/api-client';
import { assertNever } from '@gitbook/util-ts';

import { getSpaceSideSheetKeys, type NavigationCommentsSideSheet } from './side-sheets';
import {
    type LocationDescriptor,
    type NavigationScreenAccountSettings,
    type NavigationScreenChangeRequestsFilters,
    type NavigationScreenCollectionState,
    type NavigationScreenIntegrationState,
    type NavigationScreenIntegrationsState,
    type NavigationScreenOrgSettings,
    type NavigationScreenSiteState,
    type NavigationScreenSitesListState,
    type NavigationScreenSpaceState,
    type NavigationScreenState,
    NavigationSiteSettingsSections,
} from './types';

/**
 * Resolve a screen to a location.
 */
export function resolveNavigationScreenToLocation(
    screen: NavigationScreenState
): LocationDescriptor {
    const params = new URLSearchParams();
    const pathname = resolveNavigationScreenToPath(screen, params);

    getSearchForNavigationScreen(screen, params);
    const strParams = params.toString();

    return {
        pathname,
        ...(screen.hash ? { hash: screen.hash } : {}),
        ...(strParams ? { search: `?${strParams}` } : {}),
    };
}

/**
 * Resolve a screen to a href.
 */
export function resolveNavigationScreenToHref(screen: NavigationScreenState): string {
    const location = resolveNavigationScreenToLocation(screen);
    return `${location.pathname}${location.search || ''}${location.hash || ''}`;
}

/**
 * Get the search property of the location for a screen
 */
function getSearchForNavigationScreen(
    screen: NavigationScreenState,
    params: URLSearchParams
): void {
    if (typeof screen.query === 'string') {
        params.append('q', screen.query);
    }

    if (screen.askAI) {
        params.append('ask_ai', 'true');
    }

    if (screen.ssoLogin) {
        if (screen.ssoLogin.email) {
            params.append('email', screen.ssoLogin.email);
        }
        if (screen.ssoLogin.noSSO) {
            params.append('no_sso', screen.ssoLogin.noSSO);
        }
    }
}

/**
 * Get path for a navigation state.
 */
function resolveNavigationScreenToPath(
    screen: NavigationScreenState,
    query: URLSearchParams
): string {
    let result = '/';

    switch (screen.type) {
        case 'error_notfound':
            break;
        case 'home':
            if (screen.organization) {
                result += `o/${screen.organization}/home`;
            } else {
                result += 'home';
            }
            break;
        case 'join':
            result += 'join';
            break;
        case 'welcome':
            result += `welcome${screen.organizationType ? `/o/${screen.organizationType}` : ''}`;
            break;
        case 'integration':
            result += getPathForIntegrationState(screen);
            break;
        case 'integrations':
            result += getPathForIntegrationsState(screen, query);
            break;
        case 'account':
            result += getPathForAccountSettingsState(screen);
            break;
        case 'sites':
            result += getPathForSitesListState(screen, query);
            break;
        case 'site':
            result += getPathForSiteState(screen, query);
            break;
        case 'space':
            result += getPathForSpaceState(screen, query);
            break;
        case 'collection':
            result += getPathForCollectionState(screen);
            break;
        case 'org_invite':
            result += `o/${screen.organization}/invite`;
            break;
        case 'org_join':
            result += `invite/${screen.organization}/${screen.invite}`;
            break;
        case 'org_settings':
            result += getPathForOrgSettingsState(screen, query);
            break;
        case 'user':
            result += `u/${screen.user}`;
            break;
        case 'root':
            if (screen.organization) {
                result += `o/${screen.organization}`;
            }
            break;
        case 'contentkit_builder':
            result += `dev/contentkit/${encodeURIComponent(screen.content)}`;
            break;
        case 'trash':
            result += `o/${screen.organization}/trash`;
            break;
        case 'org_with_basename':
            result += `@${screen.organizationBaseName}/`;
            break;
        case 'openapi_specs': {
            if (screen.organization) {
                result += `o/${screen.organization}/`;
            }
            result += 'openapi';
            break;
        }
        case 'openapi_spec': {
            if (screen.organization) {
                result += `o/${screen.organization}/`;
            }
            result += `openapi/${screen.spec}`;
            break;
        }
        case 'change_requests': {
            result += `o/${screen.organization}/change-requests`;

            if (screen.selected) {
                result += `/s/${screen.selected.spaceId}/c/${screen.selected.changeRequestNumber}`;

                if (screen.selected.sideSheet?.type === 'comments') {
                    result = getPathForCommentsSideSheet(result, screen.selected.sideSheet, query);
                } else if (screen.selected.sideSheet?.type === 'agent') {
                    result = appendPath(result, '~/agent');
                }

                if (screen.selected.pageId) {
                    query.set('page', screen.selected.pageId);
                }
            }

            if (screen.filters) {
                appendQueryParamsForChangeRequestsFilters(query, screen.filters);
            }
            break;
        }
        case 'agents': {
            result += `o/${screen.organization}/agents`;
            break;
        }
        case 'translations': {
            if (screen.organization) {
                result += `o/${screen.organization}/`;
            }
            result += 'translations';
            if (screen.view === 'glossary') {
                result += '/glossary';
            }
            break;
        }
        default:
            assertNever(screen);
    }

    return result;
}

function getPathForOrgSettingsState(
    state: NavigationScreenOrgSettings,
    query: URLSearchParams
): string {
    let pathname = `o/${state.organization}/settings`;

    switch (state.tab.type) {
        case 'members':
            pathname += '/members';
            if (state.tab.memberId) {
                pathname += `/${state.tab.memberId}`;
                if (state.tab.detailsTab) {
                    pathname += `/${state.tab.detailsTab}`;
                }
            }
            break;

        case 'teams':
            pathname += '/teams';
            if (state.tab.teamId) {
                pathname += `/${state.tab.teamId}`;
                if (state.tab.detailsTab) {
                    pathname += `/${state.tab.detailsTab}`;
                }
            }
            break;

        case 'profile':
            break;

        case 'plans':
            pathname += '/plans';
            if (state.tab.interval) {
                query.append('interval', state.tab.interval);
            }
            if (state.tab.switchToProduct) {
                query.append('switchTo', state.tab.switchToProduct);
            }
            break;
        case 'billing':
            pathname += '/billing';
            if (state.tab.returnScreen) {
                query.append(
                    'returnScreen',
                    encodeURIComponent(
                        resolveNavigationScreenToPath(state.tab.returnScreen, new URLSearchParams())
                    )
                );
            }
            if (state.tab.publishSiteId) {
                query.append('publishSiteId', state.tab.publishSiteId);
            }
            if (state.tab.changeSiteType) {
                query.append(
                    'changeSiteType',
                    `${state.tab.changeSiteType.siteId}:${state.tab.changeSiteType.siteType}`
                );
            }
            break;
        default:
            pathname += `/${state.tab.type}`;
            break;
    }

    return pathname;
}

function getPathForAccountSettingsState(state: NavigationScreenAccountSettings): string {
    return `account${state.tab === 'profile' ? '' : `/${state.tab}`}`;
}

function getPathForSiteState(state: NavigationScreenSiteState, query: URLSearchParams): string {
    let pathname = '';

    if (state.billing) {
        query.set('billing', state.billing);
    }

    if (state.organization) {
        pathname += `o/${state.organization}/`;
    }

    pathname += `sites/${state.site}`;

    if (state.tab?.type) {
        pathname += `/${state.tab.type}`;

        if (state.tab.type === 'insights') {
            pathname += `/${state.tab.report ?? 'traffic'}`;

            if (state.tab.filters) {
                if (state.tab.filters) {
                    query.set('filters', rison.encode_object(state.tab.filters));
                }
            }
        }

        if (state.tab.type === 'settings') {
            pathname += `/${state.tab.section ?? NavigationSiteSettingsSections[0]}`;
        }

        if (state.tab.type === 'customization') {
            if (state.tab.siteSpaceId) {
                pathname += `/v/${state.tab.siteSpaceId}`;
            }

            pathname += `/${state.tab.tab}`;
        }
    }

    if (state.sideSheet) {
        if (state.sideSheet.type === 'integrations') {
            pathname = getPathForIntegrationsModalState(pathname, state, query);
        } else {
            pathname = appendPath(pathname, `~/${state.sideSheet.type}`);

            if ('siteSpaceId' in state.sideSheet && state.sideSheet.siteSpaceId) {
                pathname += `/v/${state.sideSheet.siteSpaceId}`;
            }

            if ('tab' in state.sideSheet) {
                pathname += `/${state.sideSheet.tab}`;
            }
        }
    }

    return pathname;
}

function getPathForSitesListState(
    state: NavigationScreenSitesListState,
    query: URLSearchParams
): string {
    let pathname = '';

    if (state.organization) {
        pathname += `o/${state.organization}/`;
    }

    pathname += 'sites';

    if (state.status === 'published' || state.status === 'unpublished') {
        query.set('status', state.status);
    } else {
        query.delete('status');
    }

    if (state.search) {
        query.set('search', state.search);
    } else {
        query.delete('search');
    }

    return pathname;
}

function getPathForSpaceState(state: NavigationScreenSpaceState, query: URLSearchParams): string {
    let pathname = '';

    if (state.organization) {
        pathname += `o/${state.organization}/`;
    }

    pathname += `s/${state.space}/`;

    if (state.mode) {
        pathname += `~/${state.mode}/`;
    }

    if (state.changeRequest) {
        pathname += `~/changes/${state.changeRequest}/`;
    }

    if (state.revision) {
        pathname += `~/revisions/${state.revision}/`;
    }

    if (state.reusableContent) {
        pathname += `~/reusable/${state.reusableContent}/`;
    }

    pathname += `${state.page && state.page[0] === '/' ? state.page.slice(1) : state.page}`;

    if (state.sideSheet) {
        const keys = getSpaceSideSheetKeys(state);

        // @ts-ignore
        if (!keys.includes(state.sideSheet.type)) {
            if (process.env.NODE_ENV === 'development') {
                throw new Error(
                    `Development error: "${state.sideSheet.type}" is not a valid side-sheet for the current space screen ${JSON.stringify(state)}. It can indicate that a button is rendered that could generate an invalid link.`
                );
            }
            return pathname;
        }

        if (state.sideSheet.type === 'comments') {
            pathname = getPathForCommentsSideSheet(pathname, state.sideSheet, query);
        } else if (state.sideSheet.type === 'audits') {
            pathname = appendPath(pathname, '~/audits');

            if (state.sideSheet.node) {
                query.append('node', state.sideSheet.node);
            }

            if (state.sideSheet.scope) {
                query.append('scope', state.sideSheet.scope);
            }

            if (state.sideSheet.status) {
                query.append('status', state.sideSheet.status);
            }
        } else if (state.sideSheet.type === 'integrations') {
            pathname = getPathForIntegrationsModalState(pathname, state, query);
        } else if (state.sideSheet.type === 'change-requests') {
            pathname = appendPath(pathname, '~/change-requests');

            const { status = 'open', ...otherFilters } = state.sideSheet.filters ?? {};
            pathname = appendPath(pathname, status);
            appendQueryParamsForChangeRequestsFilters(query, otherFilters);
        } else {
            pathname = appendPath(pathname, `~/${state.sideSheet.type}`);

            if ('tab' in state.sideSheet) {
                pathname += `/${state.sideSheet.tab}`;
            }
        }
    }

    return pathname;
}

function getPathForCollectionState(state: NavigationScreenCollectionState): string {
    let pathname = '';

    if (state.organization) {
        pathname += `o/${state.organization}/`;
    }

    pathname += `c/${state.collection}`;

    if (state.sideSheet) {
        pathname = appendPath(pathname, `~/${state.sideSheet.type}`);

        if ('tab' in state.sideSheet) {
            pathname += `/${state.sideSheet.tab}`;
        }
    }

    return pathname;
}

function getPathForIntegrationsState(
    state: NavigationScreenIntegrationsState,
    query: URLSearchParams
): string {
    let pathname = '';

    if (state.organization) {
        pathname += `o/${state.organization}/`;
    }

    pathname += 'integrations';

    if (state.category && Object.values(IntegrationCategory).includes(state.category)) {
        query.set('category', state.category);
    } else {
        query.delete('category');
    }

    if (state.search) {
        query.set('search', state.search);
    } else {
        query.delete('search');
    }

    if (state.provider) {
        query.set('provider', state.provider);
    } else {
        query.delete('provider');
    }

    return pathname;
}

function getPathForIntegrationState(state: NavigationScreenIntegrationState): string {
    let pathname = '';

    if (state.organization) {
        pathname += `o/${state.organization}/`;
    }

    pathname += `integrations/${state.integration}`;

    if (state.tab === 'installations') {
        pathname += `/${state.tab}`;
    }

    if (state.installation) {
        pathname += `/~/installation/${state.installation.step}`;

        if (state.installation.context) {
            pathname += `/${state.installation.context.type}/${state.installation.context.id}`;
        }
    }

    return pathname;
}

function getPathForIntegrationsModalState(
    path: string,
    state: NavigationScreenSpaceState | NavigationScreenSiteState,
    query: URLSearchParams
): string {
    if (!state.sideSheet) {
        return '';
    }

    let pathname = appendPath(path, `~/${state.sideSheet.type}`);

    if (state.sideSheet.type === 'integrations') {
        if (state.sideSheet.view) {
            if (state.sideSheet.view.type === 'integration') {
                pathname += `/${state.sideSheet.view.integration}`;

                switch (state.sideSheet.view.state) {
                    case 'configure':
                        pathname += '/configure';
                        break;
                    default:
                        pathname += '';
                        break;
                }

                return pathname;
            }

            if (state.sideSheet.view.type === 'installations') {
                pathname += '/installations';
            } else if (state.sideSheet.view.type === 'category') {
                pathname += '/category';

                const { category, search, provider } = state.sideSheet.view;

                if (category && Object.values(IntegrationCategory).includes(category)) {
                    pathname += `/${category}`;
                } else {
                    pathname += '/all';
                }

                if (search) {
                    query.set('search', search);
                } else {
                    query.delete('search');
                }

                if (provider) {
                    query.set('provider', provider);
                } else {
                    query.delete('provider');
                }
            }
        } else {
            pathname += '/installations';
        }
    }

    return pathname;
}

function getPathForCommentsSideSheet(
    path: string,
    sideSheet: NavigationCommentsSideSheet,
    query: URLSearchParams
): string {
    let pathname = appendPath(path, '~/comments');

    if (sideSheet.comment) {
        pathname = appendPath(pathname, sideSheet.comment);
    }

    if (sideSheet.context && ['preview'].includes(sideSheet.context)) {
        query.append('context', sideSheet.context);
    }

    if (sideSheet.node) {
        query.append('node', sideSheet.node);
    }

    if (sideSheet.tableCell) {
        query.append('record', sideSheet.tableCell.record);
        query.append('definition', sideSheet.tableCell.definition);
    }

    return pathname;
}

function appendQueryParamsForChangeRequestsFilters(
    query: URLSearchParams,
    filters: NavigationScreenChangeRequestsFilters
): void {
    if (filters.status) {
        query.set('status', filters.status);
    }
    if (filters.contributor) {
        query.set('contributor', filters.contributor);
    }
    if (filters.creator) {
        query.set('creator', filters.creator);
    }
    if (filters.requestedReview) {
        query.set('requestedReview', filters.requestedReview);
    }
    if (filters.conversationsIssuesCluster) {
        query.set('conversationsIssuesCluster', filters.conversationsIssuesCluster);
    }
    if (filters.space) {
        query.set('space', filters.space);
    }
}

/** Append two paths */
function appendPath(base: string, more: string): string {
    return `${base}${base[base.length - 1] === '/' ? '' : '/'}${more}`;
}
